import { createContext, useEffect, useRef } from 'react';
import useAuth from '@assetron/common/hooks/useAuth';
import Loader from '@assetron/web-common/components/common/Loader';
import { RouterProvider } from 'react-router-dom';
import { createLogger } from '@assetron/common/telemetry/logger';
import Router from '../routes';
import useGoogleOneTap from '../hooks/useGoogleOneTap';

const RouteContext = createContext(undefined);
const routeLogger = createLogger('router.auth');

export function RouteContextProvider() {
  const { isUserAuthenticated } = useAuth();
  const { isProcessing } = useGoogleOneTap();
  const router = Router(isUserAuthenticated);
  const previousLocationKeyRef = useRef<string | null>(null);
  const navigationStartRef = useRef<number | null>(null);

  useEffect(() => {
    const initialLocation = router.state.location;
    const initialLocationKey =
      initialLocation.key ??
      `${initialLocation.pathname}${initialLocation.search}${initialLocation.hash}`;
    previousLocationKeyRef.current = initialLocationKey;

    routeLogger.info('route_loaded', {
      pathname: initialLocation.pathname,
      search: initialLocation.search || undefined,
      hash: initialLocation.hash || undefined,
      isAuthenticated: isUserAuthenticated,
    });

    const unsubscribe = router.subscribe((state) => {
      const nextLocation = state.location;
      const nextLocationKey =
        nextLocation.key ??
        `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;

      if (
        state.navigation.state !== 'idle' &&
        navigationStartRef.current === null
      ) {
        navigationStartRef.current = performance.now();
        routeLogger.debug('route_navigation_started', {
          from: previousLocationKeyRef.current,
          to: state.navigation.location?.pathname ?? nextLocation.pathname,
          navigationState: state.navigation.state,
          isAuthenticated: isUserAuthenticated,
        });
      }

      if (nextLocationKey === previousLocationKeyRef.current) {
        return;
      }

      const durationMs =
        navigationStartRef.current === null
          ? undefined
          : Math.round(performance.now() - navigationStartRef.current);
      navigationStartRef.current = null;

      routeLogger.info('route_changed', {
        pathname: nextLocation.pathname,
        search: nextLocation.search || undefined,
        hash: nextLocation.hash || undefined,
        durationMs,
        navigationState: state.navigation.state,
        isAuthenticated: isUserAuthenticated,
      });

      previousLocationKeyRef.current = nextLocationKey;
    });

    return unsubscribe;
  }, [router, isUserAuthenticated]);

  if (isProcessing) {
    return (
      <div data-testid="route-processing-loader">
        <Loader />
      </div>
    );
  }

  return (
    <RouteContext.Provider value={undefined}>
      <RouterProvider router={router} />
    </RouteContext.Provider>
  );
}
